import { useRouter } from "next/router";
import { FC } from "react";

import type { RecommendationListItem } from "@src/features/guestFeedback/GuestFeedbackView";
import RecommendationBrandLogo from "@src/features/guestFeedback/RecommendationBrandLogo";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import CircleCheckmarkFillIcon from "@src/lib/icons/18px/circle-checkmark-fill";
import InboxIcon from "@src/lib/icons/18px/inbox";
import LineChartIcon from "@src/lib/icons/18px/line-chart";
import PinOutlineIcon from "@src/lib/icons/18px/pin-outline";
import ProgressIndicator2FillIcon from "@src/lib/icons/18px/progress-indicator-2-fill";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { GuestFeedbackCorrectiveActionStatus } from "@src/types.generated";

type BoardColumnConfig = {
  status: GuestFeedbackCorrectiveActionStatus;
  label: string;
  Icon: FC<{ className?: string }>;
  accentClassName: string;
};

// The "All" board mirrors the active recommendation statuses. Rejected is
// surfaced through the separate "Dismissed" nav tab, so it has no column here.
const BOARD_COLUMNS: BoardColumnConfig[] = [
  {
    status: GuestFeedbackCorrectiveActionStatus.Pending,
    label: "Inbox",
    Icon: InboxIcon,
    accentClassName: "text-gray-12",
  },
  {
    status: GuestFeedbackCorrectiveActionStatus.InProgress,
    label: "In progress",
    Icon: ProgressIndicator2FillIcon,
    accentClassName: "text-blue-9",
  },
  {
    status: GuestFeedbackCorrectiveActionStatus.MeasuringImpact,
    label: "Measuring impact",
    Icon: LineChartIcon,
    accentClassName: "text-purple-7",
  },
  {
    status: GuestFeedbackCorrectiveActionStatus.Completed,
    label: "Done",
    Icon: CircleCheckmarkFillIcon,
    accentClassName: "text-green-9",
  },
];

const getLocationLabel = (locations: string[]): string =>
  locations.length === 1 ? locations[0] : `${locations.length} locations`;

const formatActionDate = (value: string): string =>
  new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const parseDisplayDate = (value: string): Date => {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }
  return new Date(value);
};

const relativeDaysAgo = (value: string): string => {
  const days = Math.max(
    0,
    Math.floor((Date.now() - parseDisplayDate(value).getTime()) / 86_400_000),
  );
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

// Footer meta per state: Done cards read as closed out (checkmark + the date
// completed); Measuring-impact cards read as in-flight ("Implemented X days
// ago" from when training was assigned) — no checkmark, since they're not done.
type CardMeta = { text: string; showCheck: boolean };

const getCardMeta = (
  recommendation: RecommendationListItem,
): CardMeta | null => {
  if (
    recommendation.status === GuestFeedbackCorrectiveActionStatus.Completed &&
    recommendation.completedAt
  ) {
    return {
      text: formatActionDate(recommendation.completedAt),
      showCheck: true,
    };
  }
  if (
    recommendation.status ===
      GuestFeedbackCorrectiveActionStatus.MeasuringImpact &&
    recommendation.measuringImpactStartedOn
  ) {
    return {
      text: `Implemented ${relativeDaysAgo(recommendation.measuringImpactStartedOn)}`,
      showCheck: false,
    };
  }
  if (
    recommendation.status === GuestFeedbackCorrectiveActionStatus.InProgress &&
    recommendation.acceptedAt
  ) {
    return {
      text: `Started ${relativeDaysAgo(recommendation.acceptedAt)}`,
      showCheck: false,
    };
  }
  return null;
};

const CardBody: FC<{
  recommendation: RecommendationListItem;
  showBrandLogo: boolean;
}> = ({ recommendation, showBrandLogo }) => {
  const meta = getCardMeta(recommendation);

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex w-fit items-center gap-[3px] rounded-lg border border-gray-3 bg-background px-[7px] py-[3px]">
          <PinOutlineIcon className="size-3 text-gray-12" />
          <Text variant="fine-print-title" className="text-gray-12">
            {getLocationLabel(recommendation.affectedLocations)}
          </Text>
        </span>
        {showBrandLogo ? (
          <RecommendationBrandLogo recommendation={recommendation} />
        ) : null}
      </div>
      <Text
        variant="compact-description"
        multiline
        className="line-clamp-2 text-gray-12"
      >
        {recommendation.title}
      </Text>
      {meta ? (
        <div className="flex items-center gap-1">
          {meta.showCheck ? (
            <CheckmarkIcon className="size-3 text-muted-foreground" />
          ) : null}
          <Text
            variant="fine-print-description"
            className="text-muted-foreground"
          >
            {meta.text}
          </Text>
        </div>
      ) : null}
    </>
  );
};

const CARD_CLASS =
  "flex flex-col gap-1 rounded-lg bg-gray-1 px-3 py-2.5 shadow-[0px_1px_1px_rgba(0,0,0,0.05)]";

const RecommendationCard: FC<{
  recommendation: RecommendationListItem;
  onOpen: (id: string) => void;
  showBrandLogo: boolean;
}> = ({ recommendation, onOpen, showBrandLogo }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        CARD_CLASS,
        "cursor-pointer transition-colors hover:bg-gray-2",
      )}
      onClick={() => onOpen(recommendation.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(recommendation.id);
        }
      }}
    >
      <CardBody recommendation={recommendation} showBrandLogo={showBrandLogo} />
    </div>
  );
};

const BoardColumn: FC<{
  column: BoardColumnConfig;
  recommendations: RecommendationListItem[];
  onOpen: (id: string) => void;
  showBrandLogo: boolean;
}> = ({ column, recommendations, onOpen, showBrandLogo }) => {
  const Icon = column.Icon;

  return (
    <section className="flex w-72 shrink-0 flex-col rounded-lg border border-gray-3">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-[7px]">
          <Icon className={cn("size-[18px]", column.accentClassName)} />
          <Text variant="compact-title" className={column.accentClassName}>
            {column.label}
          </Text>
        </div>
        <Text variant="fine-print-title" className="text-gray-12 tabular-nums">
          {recommendations.length}
        </Text>
      </div>
      <div className="flex min-h-16 flex-col gap-2 p-2">
        {recommendations.map((rec) => (
          <RecommendationCard
            key={rec.id}
            recommendation={rec}
            onOpen={onOpen}
            showBrandLogo={showBrandLogo}
          />
        ))}
      </div>
    </section>
  );
};

const RecommendationsBoard: FC<{
  recommendations: RecommendationListItem[];
  showBrand: boolean;
}> = ({ recommendations, showBrand }) => {
  const router = useRouter();

  const openRecommendation = (id: string) => {
    void router.push({
      pathname: "/guest-feedback/recommendations/[id]",
      query: { id },
    });
  };

  return (
    <div className="flex items-start gap-3 overflow-x-auto pb-2">
      {BOARD_COLUMNS.map((column) => (
        <BoardColumn
          key={column.status}
          column={column}
          recommendations={recommendations.filter(
            (rec) => rec.status === column.status,
          )}
          onOpen={openRecommendation}
          showBrandLogo={showBrand}
        />
      ))}
    </div>
  );
};

export default RecommendationsBoard;
